var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Amount } from './Amount.js';
import { getRepresentativeValue, normalizeName } from '../utils.js';
import { AccountBalancesContainer } from './BalancesContainerAccount.js';
import { Balance } from './Balance.js';
import { BalancesDataTableBuilder } from '../builders/BalancesDataTableBuilder.js';
/** @internal */
export class GroupBalancesContainer {
    constructor(parent, balancesReport, payload) {
        this.parent = parent;
        this.balancesReport = balancesReport;
        this.payload = payload;
    }
    getBalancesReport() {
        return this.balancesReport;
    }
    getName() {
        return this.payload.name;
    }
    getNormalizedName() {
        return this.payload.normalizedName;
    }
    getGroup() {
        return __awaiter(this, void 0, void 0, function* () {
            const group = yield this.balancesReport.getBook().getGroup(this.getNormalizedName());
            return group || null;
        });
    }
    getAccount() {
        return __awaiter(this, void 0, void 0, function* () {
            return null;
        });
    }
    getParent() {
        return this.parent;
    }
    getDepth() {
        if (this.depth == null) {
            const parent = this.getParent();
            this.depth = parent != null ? parent.getDepth() + 1 : 0;
        }
        return this.depth;
    }
    isCredit() {
        return this.payload.credit || false;
    }
    isPermanent() {
        return this.payload.permanent || false;
    }
    isFromAccount() {
        return false;
    }
    isFromGroup() {
        return true;
    }
    hasGroupBalances() {
        const groupContainers = this.getGroupContainers();
        return groupContainers != null && groupContainers.length > 0;
    }
    getCumulativeBalance() {
        return getRepresentativeValue(new Amount(this.payload.cumulativeBalance || 0), this.isCredit());
    }
    getCumulativeBalanceRaw() {
        return new Amount(this.payload.cumulativeBalance || 0);
    }
    getCumulativeCredit() {
        return new Amount(this.payload.cumulativeCredit || 0);
    }
    getCumulativeDebit() {
        return new Amount(this.payload.cumulativeDebit || 0);
    }
    getCumulativeBalanceText() {
        return this.balancesReport.getBook().formatValue(this.getCumulativeBalance());
    }
    getCumulativeBalanceRawText() {
        return this.balancesReport.getBook().formatValue(this.getCumulativeBalanceRaw());
    }
    getCumulativeCreditText() {
        return this.balancesReport.getBook().formatValue(this.getCumulativeCredit());
    }
    getCumulativeDebitText() {
        return this.balancesReport.getBook().formatValue(this.getCumulativeDebit());
    }
    getPeriodBalance() {
        return getRepresentativeValue(new Amount(this.payload.periodBalance || 0), this.isCredit());
    }
    getPeriodBalanceRaw() {
        return new Amount(this.payload.periodBalance || 0);
    }
    getPeriodCredit() {
        return new Amount(this.payload.periodCredit || 0);
    }
    getPeriodDebit() {
        return new Amount(this.payload.periodDebit || 0);
    }
    getPeriodBalanceText() {
        return this.balancesReport.getBook().formatValue(this.getPeriodBalance());
    }
    getPeriodBalanceRawText() {
        return this.balancesReport.getBook().formatValue(this.getPeriodBalanceRaw());
    }
    getPeriodCreditText() {
        return this.balancesReport.getBook().formatValue(this.getPeriodCredit());
    }
    getPeriodDebitText() {
        return this.balancesReport.getBook().formatValue(this.getPeriodDebit());
    }
    getBalances() {
        if (!this.payload.balances) {
            return new Array();
        }
        return this.payload.balances.map(balancePlain => new Balance(this, balancePlain));
    }
    createDataTable() {
        return new BalancesDataTableBuilder(this.balancesReport.getBook(), this.getBalancesContainers(), this.balancesReport.getPeriodicity());
    }
    getProperties() {
        return this.payload.properties != null ? Object.assign({}, this.payload.properties) : {};
    }
    getProperty(...keys) {
        for (let index = 0; index < keys.length; index++) {
            const key = keys[index];
            let value = this.payload.properties != null ? this.payload.properties[key] : null;
            if (value != null && value.trim() != '') {
                return value;
            }
        }
        return undefined;
    }
    getPropertyKeys() {
        let properties = this.getProperties();
        let propertyKeys = [];
        if (properties) {
            for (var key in properties) {
                if (Object.prototype.hasOwnProperty.call(properties, key)) {
                    propertyKeys.push(key);
                }
            }
        }
        propertyKeys = propertyKeys.sort();
        return propertyKeys;
    }
    getBalancesContainers() {
        let containers = [];
        const groupContainers = this.getGroupContainers();
        if (groupContainers && groupContainers.length > 0) {
            containers = containers.concat(groupContainers);
        }
        const accountContainers = this.getAccountContainers();
        if (accountContainers && accountContainers.length > 0) {
            containers = containers.concat(accountContainers);
        }
        return containers;
    }
    /** @internal */
    getGroupContainers() {
        let groupBalances = this.payload.groupBalances;
        if (this.groupBalances == null && groupBalances != null) {
            this.groupBalances = [];
            for (let i = 0; i < groupBalances.length; i++) {
                const groupBalance = groupBalances[i];
                this.groupBalances.push(new GroupBalancesContainer(this, this.balancesReport, groupBalance));
            }
        }
        return this.groupBalances || [];
    }
    /** @internal */
    getAccountContainers() {
        let accountBalances = this.payload.accountBalances;
        if (this.accountBalances == null && accountBalances != null) {
            this.accountBalances = [];
            for (let i = 0; i < accountBalances.length; i++) {
                const accountBalance = accountBalances[i];
                this.accountBalances.push(new AccountBalancesContainer(this, this.balancesReport, accountBalance));
            }
        }
        return this.accountBalances || [];
    }
    getBalancesContainer(name) {
        var _a;
        const normalizedName = normalizeName(name);
        const rootContainers = this.getBalancesContainers();
        if (rootContainers == null || rootContainers.length === 0) {
            throw `${name} not found on group ${this.getName()}`;
        }
        if (this.balancesContainersMap == null) {
            this.balancesContainersMap = this.fillBalancesContainersMap(new Map(), rootContainers);
        }
        const balancesContainer = (_a = this.balancesContainersMap) === null || _a === void 0 ? void 0 : _a.get(normalizedName);
        if (!balancesContainer) {
            throw `${name} not found on group ${this.getName()}`;
        }
        return balancesContainer;
    }
    /** @internal */
    fillBalancesContainersMap(map, containers) {
        for (let i = 0; i < containers.length; i++) {
            const container = containers[i];
            const normalizedName = container.getNormalizedName();
            if (normalizedName) {
                if (!map.has(normalizedName)) {
                    map.set(normalizedName, container);
                }
            }
            const nextContainers = container.getBalancesContainers();
            if (nextContainers && nextContainers.length > 0) {
                this.fillBalancesContainersMap(map, nextContainers);
            }
        }
        return map;
    }
}
//# sourceMappingURL=BalancesContainerGroup.js.map